// P-8 (DA-BUG-1035): In-App URL rules — portal mirror of the RRS publish check (vectors:
// ./in-app-url-vectors.json). kind "https": open_url targets, deep-link fallbacks, media (image,
// video, poster, carousel slide, product) and consent links must be https. kind "deeplink": a
// deep_link target may be https or one of the tenant's configured schemes (In-App settings
// `deep_link_schemes`); with no schemes configured, any well-formed custom scheme. Never allowed:
export const FORBIDDEN_SCHEMES = ["javascript", "data", "vbscript", "file", "about", "blob", "http", "content", "intent"];

const SCHEME_NAME = /^[a-z][a-z0-9+.-]*$/;
const WITH_SCHEME = /^([A-Za-z][A-Za-z0-9+.-]*):(.*)$/s;

// A scheme name as stored in settings: lower-case, no ":" or "//", not forbidden, not https.
export function normalizeScheme(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const s = v.trim().toLowerCase().replace(/:(\/\/)?$/, "");
  if (!SCHEME_NAME.test(s) || s === "https" || FORBIDDEN_SCHEMES.includes(s)) return null;
  return s;
}

// Why this URL is not allowed, or null when it is. An empty URL is not judged here (callers
// report a missing URL themselves).
export function urlProblem(url: unknown, kind: "https" | "deeplink", schemes: string[] = []): string | null {
  const u = String(url ?? "").trim();
  if (!u) return null;
  // Same rule as RRS (DA-BUG-1157) and both SDKs: whitespace, C0 controls and DEL are refused;
  // C1 (U+0080–009F) is not. Code-point check instead of control escapes in the regex (Sonar S6324).
  if (/\s/.test(u) || Array.from(u).some((ch) => { const cp = ch.codePointAt(0) as number; return cp < 0x20 || cp === 0x7f; })) {
    return "is not a valid URL (it contains spaces or control characters)";
  }
  const m = WITH_SCHEME.exec(u);
  if (!m) return "must start with https://";
  const scheme = m[1].toLowerCase();
  if (FORBIDDEN_SCHEMES.includes(scheme)) return `"${scheme}:" links are not allowed — use an https:// link`;
  if (scheme === "https") {
    try {
      const parsed = new URL(u);
      return parsed.hostname ? null : "is not a valid https URL";
    } catch {
      return "is not a valid https URL";
    }
  }
  if (kind === "https") return "must be an https:// link";
  if (!m[2]) return "is not a valid deep link";
  const allowed = (schemes || []).map((s) => String(s).toLowerCase());
  if (allowed.length && !allowed.includes(scheme)) {
    return `uses "${scheme}:", which is not an allowed deep-link scheme (In-App settings: ${allowed.join(", ")})`;
  }
  return null;
}
